import React, { useEffect, useRef } from 'react'
import { Field, Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { Brand } from 'src/redux/brandsReducer'
import { MobileReferenceValue } from 'src/redux/mobileReferenceReducer'
import { specificMobileError } from 'src/helpers/mobileApiError'
import { MobilePromotionFormData, validateMobilePromotionForm } from 'src/helpers/mobilePromotionFormValidation'
import FormMonthPicker from '../atoms/FormMonthPicker'
import FormSelect, { FormSelectOption } from '../atoms/FormSelect'
import { FormFieldError, FormFieldLabel, FormTextInput } from '../atoms/FormTextInput'
import MobileDescriptionEditor from './MobileDescriptionEditor'
import FormFooter from './FormFooter'
import InvalidFieldScroll from './InvalidFieldScroll'
import { YesNoSegmentField } from './YesNoSegment'
import { referenceSelectOptions } from './promotionReferenceOptions'

interface MobilePromotionFormProps {
  initialValues: MobilePromotionFormData
  brands: Brand[]
  referenceColors?: MobileReferenceValue[]
  referenceCategories?: MobileReferenceValue[]
  referenceGearBoxes?: MobileReferenceValue[]
  referenceDriveTypes?: MobileReferenceValue[]
  referenceVatRates?: MobileReferenceValue[]
  onSubmit: (values: MobilePromotionFormData) => void
  onCancel: () => void
  loading?: boolean
  error?: boolean
  submitLabel?: string
  errorMessage?: string
  apiError?: string | null
}

interface FieldMeta {
  error?: string
  touched?: boolean
  submitFailed?: boolean
}

const isFieldInvalid = (meta: FieldMeta): boolean =>
  Boolean(meta.error) && (Boolean(meta.touched) || Boolean(meta.submitFailed))

const fieldClass = 'self-stretch flex flex-col justify-start items-start gap-2'

const SectionTitle = ({ label }: { label: string }): JSX.Element => {
  return <div className="col-span-2 text-zinc-900 text-base font-semibold font-Montserrat leading-6">{label}</div>
}

interface TextFieldProps {
  name: keyof MobilePromotionFormData
  label: string
  placeholder: string
  month?: boolean
}

const TextField = ({ name, label, placeholder, month = false }: TextFieldProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <Field name={name}>
      {({ input, meta }) => {
        const fieldValue = typeof input.value === 'string' ? input.value : ''
        const invalid = isFieldInvalid(meta)
        return (
          <div className={fieldClass} data-field={name}>
            <FormFieldLabel label={label} />
            {month ? (
              <FormMonthPicker value={fieldValue} onChange={input.onChange} placeholder={placeholder} error={invalid} />
            ) : (
              <FormTextInput value={fieldValue} onChange={input.onChange} placeholder={placeholder} error={invalid} />
            )}
            <FormFieldError show={invalid} message={t('my_vehicles.field_required')} />
          </div>
        )
      }}
    </Field>
  )
}

interface SelectFieldProps {
  name: keyof MobilePromotionFormData
  label: string
  options: FormSelectOption[]
  clearable?: boolean
}

const SelectField = ({ name, label, options, clearable = true }: SelectFieldProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <Field name={name}>
      {({ input, meta }) => (
        <div className={fieldClass} data-field={name}>
          <FormFieldLabel label={label} />
          <FormSelect
            value={typeof input.value === 'string' ? input.value : ''}
            onChange={input.onChange}
            placeholder={t('my_vehicles.choose')}
            clearable={clearable}
            error={isFieldInvalid(meta)}
            options={options}
          />
          <FormFieldError show={isFieldInvalid(meta)} message={t('my_vehicles.field_required')} />
        </div>
      )}
    </Field>
  )
}

const MobilePromotionForm = ({
  initialValues,
  brands,
  referenceColors = [],
  referenceCategories = [],
  referenceGearBoxes = [],
  referenceDriveTypes = [],
  referenceVatRates = [],
  onSubmit,
  onCancel,
  loading = false,
  error = false,
  submitLabel,
  errorMessage,
  apiError
}: MobilePromotionFormProps): JSX.Element => {
  const { t } = useTranslation()
  const errorBannerRef = useRef<HTMLDivElement>(null)
  const submitText = submitLabel ?? t('my_vehicles.add_promotion')
  const errorText = specificMobileError(apiError, t) ?? errorMessage ?? t('my_vehicles.promotion_create_error')

  useEffect(() => {
    if (!error) {
      return
    }
    errorBannerRef.current?.scrollIntoView({ block: 'center' })
  }, [error, errorText])
  const labelForCode = (group: 'promotion_colors' | 'promotion_categories', code: string): string => {
    const key = `my_vehicles.${group}.${code}` as 'my_vehicles.promotion_colors.BLACK'
    const translated = t(key)
    return translated === key ? code : translated
  }
  const categoryOptions = referenceSelectOptions({
    values: referenceCategories,
    labelForCode: (code) => labelForCode('promotion_categories', code),
    currentCode: initialValues.category
  })
  const colorOptions = referenceSelectOptions({
    values: referenceColors,
    labelForCode: (code) => labelForCode('promotion_colors', code),
    currentCode: initialValues.color
  })
  const driveTypeOptions = referenceSelectOptions({
    values: referenceDriveTypes,
    labelForCode: (code) => code,
    currentCode: initialValues.drive_type
  })
  const gearBoxOptions = referenceSelectOptions({
    values: referenceGearBoxes,
    labelForCode: (code) => code,
    currentCode: initialValues.gear_box
  })
  const catalogVatRates = referenceSelectOptions({
    values: referenceVatRates,
    labelForCode: (code) => code,
    currentCode: initialValues.vat_rate
  })
  const vatRateOptions = [
    ...catalogVatRates.filter((option) => option.value !== 'not_deductible'),
    { value: 'not_deductible', label: t('my_vehicles.promotion_vat_none') }
  ]
  const brandOptions = brands.map((brand) => ({ value: String(brand.id), label: brand.name }))

  return (
    <Form
      onSubmit={onSubmit}
      validate={validateMobilePromotionForm}
      initialValues={initialValues}
      render={({ handleSubmit, submitting, dirty, submitFailed, errors }) => (
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit}
          className="self-stretch flex-1 min-h-0 relative bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-center overflow-hidden"
          style={{
            boxShadow:
              '0px 4px 8px 0px rgba(0,0,0,0.08), 0px 2px 4px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'
          }}>
          <div className="self-stretch h-px bg-borders-border-base" />
          <InvalidFieldScroll submitFailed={Boolean(submitFailed)} errors={errors ?? {}} />
          <div className="w-full max-w-[1000px] min-w-[720px] px-4 pt-4 pb-6 flex-1 min-h-0 flex flex-col justify-start items-start gap-6 overflow-y-auto">
            <div className="text-zinc-900 text-xl font-bold font-Montserrat leading-7">
              {t('my_vehicles.promotion_form_title')}
            </div>
            {error && (
              <div
                ref={errorBannerRef}
                className="text-foregrounds-fg-error text-sm font-medium font-Montserrat leading-5">
                {errorText}
              </div>
            )}
            <div className="self-stretch grid grid-cols-2 gap-4">
              <SectionTitle label={t('my_vehicles.promotion_section_vehicle')} />
              <SelectField name="brand_id" label={t('my_vehicles.brand')} options={brandOptions} clearable={false} />
              <TextField name="model" label={t('my_vehicles.model')} placeholder="" />
              <SelectField
                name="category"
                label={t('my_vehicles.promotion_category')}
                options={categoryOptions}
                clearable={false}
              />
              <TextField name="capacity" label={t('my_vehicles.promotion_capacity')} placeholder="" />
              <TextField name="engine_power" label={t('my_vehicles.promotion_power')} placeholder="" />
              <SelectField name="drive_type" label={t('my_vehicles.promotion_drive_type')} options={driveTypeOptions} />
              <SelectField name="gear_box" label={t('my_vehicles.promotion_gear_box')} options={gearBoxOptions} />
              <SelectField name="color" label={t('my_vehicles.promotion_color')} options={colorOptions} />
              <TextField
                name="price"
                label={t('my_vehicles.promotion_price')}
                placeholder={t('my_vehicles.promotion_price_placeholder')}
              />
              <SelectField
                name="vat_rate"
                label={t('my_vehicles.promotion_vat')}
                clearable={false}
                options={vatRateOptions}
              />
              <TextField
                name="first_registration"
                label={t('my_vehicles.year')}
                placeholder={t('my_vehicles.choose')}
                month
              />
              <TextField name="total_mileage" label={t('my_vehicles.mileage')} placeholder="" />
              <TextField name="vin" label={t('my_vehicles.vin')} placeholder="" />
              <TextField name="number_of_previous_owners" label={t('my_vehicles.promotion_owners')} placeholder="" />
              <SectionTitle label={t('my_vehicles.promotion_section_equipment')} />
              <YesNoSegmentField name="metallic" label={t('my_vehicles.promotion_metallic')} />
              <YesNoSegmentField name="damage_unrepaired" label={t('my_vehicles.promotion_damage')} />
              <YesNoSegmentField name="abs" label={t('my_vehicles.promotion_abs')} />
              <YesNoSegmentField name="heated_grips" label={t('my_vehicles.promotion_heated_grips')} />
              <YesNoSegmentField name="box" label={t('my_vehicles.promotion_box')} />
              <YesNoSegmentField name="windshield" label={t('my_vehicles.promotion_windshield')} />
              <YesNoSegmentField name="kickstarter" label={t('my_vehicles.promotion_kickstarter')} />
              <SectionTitle label={t('my_vehicles.promotion_section_service')} />
              <TextField
                name="last_inspection"
                label={t('my_vehicles.promotion_last_inspection')}
                placeholder={t('my_vehicles.promotion_month_placeholder')}
              />
              <YesNoSegmentField name="new_inspection" label={t('my_vehicles.promotion_new_inspection')} />
              <YesNoSegmentField name="full_service_history" label={t('my_vehicles.promotion_service_history')} />
              <SectionTitle label={t('my_vehicles.promotion_description')} />
              <div className={`${fieldClass} col-span-2`}>
                <Field name="description">
                  {({ input }) => (
                    <>
                      <FormFieldLabel label={t('my_vehicles.promotion_description')} />
                      <MobileDescriptionEditor
                        value={typeof input.value === 'string' ? input.value : ''}
                        onChange={input.onChange}
                        placeholder={t('my_vehicles.promotion_description_placeholder')}
                      />
                    </>
                  )}
                </Field>
              </div>
              {(['highlight_1', 'highlight_2', 'highlight_3'] as const).map((name, index) => (
                <TextField
                  key={name}
                  name={name}
                  label={`${t('my_vehicles.promotion_highlight')} ${index + 1}`}
                  placeholder={t('my_vehicles.promotion_highlight_placeholder')}
                />
              ))}
            </div>
          </div>
          <FormFooter
            cancelLabel={t('my_vehicles.discard')}
            onCancel={onCancel}
            submitLabel={submitText}
            submitDisabled={submitting || loading}
            isDirty={dirty}
          />
        </form>
      )}
    />
  )
}

export default MobilePromotionForm
